import { FilterSetService } from './../../../services/filter-set.service';
import { LocalStorageService } from 'angular-2-local-storage';
import { Component, OnInit } from '@angular/core';
import { BbTableService, BbTableSelectOption, TableRow } from '../../../services/bb-table.service';
import { SapiError } from '../../../services/sapi.service';
import { Playout } from '../../../interfaces/playout';
import { AbstractListComponent } from '../../shared/abstract-list.component';
import { LocationService } from '../../../services/location.service';
import { OverlayService, NoticeDefinition } from '../../../services/overlay.service';
import { TranslateService } from '@ngx-translate/core';
import { AuthenticationService } from '../../../services/authentication.service';

@Component({
	selector: 'playout-list',
	templateUrl: './playout-list.component.html',
	styleUrls: ['./playout-list.component.scss'],
	providers: [BbTableService, FilterSetService]
})

export class PlayoutListComponent extends AbstractListComponent implements OnInit {
	logoPrefix: string;
	sessionToken: string;
	hasInactiveSelected: boolean;
	putRight: boolean;
	deleteRight: boolean;

	rootPermission: boolean;

	constructor(protected bbTableService: BbTableService,
		protected localStorageService: LocalStorageService,
		protected filterSetService: FilterSetService,
		protected overlayService: OverlayService,
		protected translate: TranslateService,
		protected authenticationService: AuthenticationService,
		protected locationService: LocationService) {

		super(bbTableService, filterSetService);

		this.logoPrefix = this.locationService.server;
		this.sessionToken = this.localStorageService.get('ovp-sessionToken');
		this.hasInactiveSelected = false;

		this.listConfiguration = {
			name: 'playout',
			basePath: '/library/playout',
			activePath: '/library/playouts'
		};

		this.filterConfig = {
			search: true,
			filters: [],
			presets: [{
				label: this.translate.instant('ALL'),
				value: 'all',
				filterSet: [],
			},
			{
				label: this.translate.instant('ACTIVE'),
				value: 'active',
				filterSet: [{
					filters: [{
						type: 'playout',
						field: 'status',
						operator: 'is',
						value: 'active'
					}]
				}],
			},
			{
				label: this.translate.instant('INACTIVE'),
				value: 'inactive',
				filterSet: [{
					filters: [{
						type: 'playout',
						field: 'status',
						operator: 'is',
						value: 'inactive'
					}]
				}],
			}]
		};
	}

	ngOnInit() {
		this.bbTableService
			.setSelect(BbTableSelectOption.multi)
			.setLimit(15)
			.sort('createddate', 'desc')
			.linkToUrl();

		this.authenticationService.hasPermission('put').subscribe((hasRight: boolean) => this.putRight = hasRight);
		this.authenticationService.hasPermission('delete').subscribe((hasRight: boolean) => this.deleteRight = hasRight);
		this.authenticationService.hasPermission('root').subscribe((hasRight: boolean) => this.rootPermission = hasRight);

		this.bbTableService.rowsSelectedObservable.subscribe((selectedRows: TableRow[]) => {
			const rows = selectedRows;
			this.hasInactiveSelected = false;
			for (const row of rows) {
				this.hasInactiveSelected = this.hasInactiveSelected || row.data.status === 'inactive';
			}
		});

		super.ngOnInit();
	}

	// override super doubleclick redirect:
	redirectToDetails(row: TableRow) {
		if (row && row.data && row.data.id) {
			// this.router.navigate([this.listConfiguration.basePath, row.data.id]);
			this.router.navigate(['library/playout', row.data.id, 'appearance']);
		}
	}

	// override deleteSelected
	deleteSelected() {
		let preventDelete = false;
		if (this.selectedRows && this.selectedRows.length) {
			this.selectedRows.forEach((playoutRow) => {
				if (playoutRow && playoutRow.data && playoutRow.data.label === 'default') {
					preventDelete = true;
				}
			});
		}
		if (preventDelete) {
			const notice: NoticeDefinition = {
				icon: 'warning',
				title: this.translate.instant('DEFAULT_PLAYOUT_CANNOT_BE_DELETED'),
				type: 'warning',
				contents: this.translate.instant('DEFAULT_PLAYOUT_CANNOT_BE_DELETED_IN_SELECTION')
			};
			this.overlayService.createNotice(notice);
		} else {
			super.deleteSelected();
		}
	}

	duplicate(playoutId: number) {
		if (playoutId) {
			this.router.navigate(['library/playout', 'new', 'appearance'], { queryParams: { fromId: playoutId } });
		}
	}

	setDefault(row?: TableRow) {
		let rows: TableRow[] = [];
		if (row) {
			rows = [row];
		} else {
			rows = this.selectedRows;
		}

		if (rows.length === 1) {
			const playout = { ...rows[0].data } as Playout;
			playout.main = true;
			const observable = this.sapiService.update('playout', playout);
			observable.subscribe(() => {
				// update success
				this.bbTableService.getRows(true); // force update all rows, since backend magic may have unset 'main' for some
				this.bbTableService.deselectAllRows();
				this.overlayService.createToast(this.translate.instant('PLAYOUT_MAIN_SET', { items: rows }), 'check');
			}, (err: SapiError) => {
				// update error
			});
		}
	}

	setStatus(row: TableRow, status: string) {
		let rows: TableRow[] = [];
		if (row) {
			rows = [row];
		} else {
			rows = this.selectedRows;
		}

		status = (status && status.toLowerCase() === 'active') ? 'active' : 'inactive'; // sanitize

		if (rows.length > 0) {
			this.overlayService.setLoading(true);
		}

		let observed = 0;
		let hasInactiveSelected = false;
		for (const row_ of rows) {
			const playout = { ...row_.data } as Playout;
			playout.status = status;
			const observable = this.sapiService.update('playout', playout);
			observable.subscribe(() => {
				// update success
				++observed;
				hasInactiveSelected = hasInactiveSelected || playout.status === 'inactive';
				this.bbTableService.setRowData(row_.id, playout);
				if (observed === rows.length) {
					this.hasInactiveSelected = hasInactiveSelected;
					this.overlayService.setLoading(false);
					this.overlayService.createToast(this.translate.instant('PLAYOUT_STATUS_TOGGLED', { items: rows }), 'check');
				}
			}, (err: SapiError) => {
				// update error
				++observed;
				hasInactiveSelected = hasInactiveSelected || playout.status === 'inactive';
				if (observed === rows.length) {
					this.hasInactiveSelected = hasInactiveSelected;
					this.overlayService.setLoading(false);
					this.overlayService.createToast(this.translate.instant('ERROR', { items: rows }), 'check');
				}
			});
		}
	}

	showData(id: number) {
		const url = this.locationService.server + '/sapi/playout/' + id + '/?sesstoken=' + this.sapiService.getSessionToken();
		const anchor = document.createElement('A');
		anchor.setAttribute('href', url);
		anchor.setAttribute('target', '_blank');

		const evt = document.createEvent('MouseEvents');
		evt.initMouseEvent('click', true, true, window, 0, 0, 0, 0, 0, false, false, false, false, 0, null);
		anchor.dispatchEvent(evt);
	}
}
